import ChatList from 'components/feature/Chat/UserChat/ChatList'
import UserChatComponent from 'components/feature/Chat/UserChat/UserChat'
import { ChatType, UserChat } from 'interfaces/UserChat'
import React, { useCallback, useEffect, useState } from 'react'
import { trackPromise } from 'react-promise-tracker'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducer'
import { ApiError } from 'services/ApiService'
import UserChatService from 'services/UserChatService'
import styled from 'styled-components'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import useMedia from 'lib/hooks/useMedia'
import { withAuth } from 'lib/validation/auth'
import { GetStaticProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Meta from 'components/modules/SEO/Meta'
import { addQueryParam, getQueryParam, removeQueryParam } from 'lib/constants/URLs'
import { parseLocale } from 'lib/constants/localization'
import { Pagination } from 'interfaces/Pagination'
import { trackEvent } from 'lib/tracking/analytics-service'
import { AnalyticsEventChatImpression } from 'lib/constants/AnalyticsEventType'
import _useTranslation from 'lib/hooks/_useTranslation'
import { ClientOnly } from 'components/elements/ClientOnly'

interface ContainerProps {
    isSelected?: boolean
}

export const Container = styled.div<ContainerProps>`
    margin-top: var(--navbar-height);

    display: grid;
    grid-template-columns: 1fr 3fr;
    gap: 20px;
    padding: 50px;

    @media (max-width: 1440px) {
        grid-template-columns: 1fr 2fr;
    }

    @media ${deviceSize.lg} {
        display: flex;
        flex-direction: ${({ isSelected }) => (isSelected ? 'column-reverse' : 'column')};
        overflow: hidden;
    }

    @media (max-width: 750px) {
        padding: 0;
    }
`

const loadingAreas = {
    container: 'chatContainer',
}

const Chat = () => {
    const [userChats, setUserChats] = useState<UserChat[]>()
    const [pagination, setPagination] = useState<Pagination>({ hasMorePages: false, page: 0 })
    const [selectedChat, setSelectedChat] = useState<UserChat>()
    const [listUnreadChats, setListUnreadChats] = useState(false)
    const [selectedChatType, setSelectedChatType] = useState<ChatType>()

    const authenticatedUser = useSelector((state: AppState) => state.user)
    const { t: tCommon } = _useTranslation('common')
    const isMobile = useMedia(deviceSize.lg)

    const isStaffUser = !!authenticatedUser?.isStaff

    useEffect(() => {
        const chatUuid = getQueryParam('chatUuid')

        if (chatUuid) {
            UserChatService.getChat(chatUuid)
                .then((userChat) => {
                    setSelectedChat(userChat)
                })
                .catch((error) => {
                    if (error instanceof ApiError) {
                        error.handleUnknownError(tCommon, 'userChat.getChat')
                    } else {
                        throw error
                    }
                })
        }
    }, [tCommon])

    const fetchData = useCallback(
        (pageNumber: number, showLoading?: boolean) => {
            if (!authenticatedUser) return

            const promise = UserChatService.list(authenticatedUser.uuid, pageNumber, listUnreadChats, selectedChatType)
                .then((response) => {
                    const responseChats = response.chats
                    if (pageNumber === 0) {
                        setUserChats(responseChats)
                    } else {
                        setUserChats((chats) => (chats ? [...chats, ...responseChats] : responseChats))
                    }
                    setPagination({ page: response.page, hasMorePages: response.hasMorePages })

                    if (!isMobile) {
                        setSelectedChat((chat) => (chat ? chat : responseChats[0]))
                    }
                })
                .catch((error) => {
                    if (error instanceof ApiError) {
                        error.handleUnknownError(tCommon, 'userChat.list')
                    } else {
                        throw error
                    }
                })

            if (showLoading) {
                trackPromise(promise, loadingAreas.container)
            }
        },
        [authenticatedUser, isMobile, listUnreadChats, tCommon, selectedChatType]
    )

    useEffect(() => {
        if (!(selectedChat && selectedChat.unreadCount > 0)) return

        UserChatService.markChatAsRead(selectedChat.uuid)
            .then(() => fetchData(0, false))
            .catch((error) => {
                if (error instanceof ApiError) {
                    error.handleUnknownError(tCommon, 'userChat.markChatAsRead')
                } else {
                    throw error
                }
            })
    }, [fetchData, selectedChat, tCommon])

    const refetchChatList = useCallback(() => fetchData(0, true), [fetchData])

    useEffect(() => {
        refetchChatList()
        // const interval = setInterval(() => {
        //     fetchData(0, false)
        // }, 30000)

        // return () => {
        //     clearInterval(interval)
        // }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listUnreadChats, selectedChatType])

    const selectChat = (chat: UserChat | undefined) => {
        if (!chat) return

        trackEvent(AnalyticsEventChatImpression, { value: chat.uuid, chat })
        removeQueryParam('prefillWithTutoringUpselling')
        addQueryParam('chatUuid', chat.uuid)
        setSelectedChat(chat)
    }

    const resetChat = () => {
        removeQueryParam('chatUuid')
        removeQueryParam('prefillWithTutoringUpselling')
        setSelectedChat(undefined)
    }

    const handleListUnreadChatChecked = (checked: boolean, type: ChatType) => {
        setListUnreadChats(checked)
        setSelectedChatType(checked ? type : undefined)
    }

    if (!isStaffUser) return null

    return (
        <ClientOnly>
            <Container isSelected={!!selectedChat}>
                <Meta title="Chat" noIndex />
                {isMobile ? (
                    !selectedChat ? (
                        <ChatList
                            userChats={userChats}
                            selectChat={selectChat}
                            selectedChatUuid={undefined}
                            area={loadingAreas.container}
                            fetchData={fetchData}
                            pagination={pagination}
                            refetchChatList={refetchChatList}
                            handleCheckboxClicked={handleListUnreadChatChecked}
                            listUnreadChats={listUnreadChats}
                            selectedChatType={selectedChatType}
                        />
                    ) : null
                ) : (
                    <ChatList
                        userChats={userChats}
                        selectChat={selectChat}
                        selectedChatUuid={selectedChat?.uuid}
                        area={loadingAreas.container}
                        fetchData={fetchData}
                        pagination={pagination}
                        refetchChatList={refetchChatList}
                        handleCheckboxClicked={handleListUnreadChatChecked}
                        listUnreadChats={listUnreadChats}
                        selectedChatType={selectedChatType}
                    />
                )}

                {isMobile ? (
                    selectedChat ? (
                        <UserChatComponent selectedChat={selectedChat} resetChat={resetChat} />
                    ) : null
                ) : (
                    <UserChatComponent selectedChat={selectedChat} />
                )}
            </Container>
        </ClientOnly>
    )
}

export const getStaticProps: GetStaticProps = async (props) => ({
    props: {
        ...(await serverSideTranslations(parseLocale(props.locale).interfaceLanguageCode, ['common', 'chat'])),
    },
})

export default withAuth(Chat, true, false)
